import { Box, Stack } from '@mui/material';

import { Button } from '@/components/form/baseInputs/Button';
import { FormAwareTypeaheadSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadSelectInput';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useForm } from '@/components/react-hook-form';
import { useReportError } from '@/hooks/useReportError';
import { isTestTenant } from '@/utils/diagnostics';
import { getNodes } from '@/utils/graphqlUtils';

import {
  useArchetypeClientsQuery,
  useCopyArchetypeClientAsyncMutation,
} from './graphql/ClientImportExport.generated';

function useCopyArchetypeOptions() {
  const queryProps = useArchetypeClientsQuery({
    // don't run this query in test; the test users aren't authed to see this
    skip: isTestTenant(),
  });
  const { data } = queryProps;

  if (isTestTenant()) {
    return {
      ...queryProps,
      options: [{ disabled: true, value: '', display: 'Disabled for tests' }],
    };
  }

  const options = getNodes(data?.archetypeHouseholds).map((household) => ({
    value: household.id,
    display: household.displayName,
    secondaryText: household.notes ?? undefined,
  }));

  return {
    ...queryProps,
    options,
  };
}

interface CopyArchetypeFormShape {
  householdId: string;
}

export function ClientArchetypeCopy() {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();

  const { options, loading: dataLoading } = useCopyArchetypeOptions();

  const [copyHousehold] = useCopyArchetypeClientAsyncMutation({
    onError: (err) => {
      showFeedback('Failed to copy client');
      reportError('could not copy client', err);
    },
  });

  const formMethods = useForm<CopyArchetypeFormShape>();
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = formMethods;

  async function onClientCopy(formValues: CopyArchetypeFormShape) {
    return copyHousehold({
      variables: {
        id: formValues.householdId,
      },
      onCompleted: () => {
        // The async copy runs in the background and returns only a boolean, so
        // there's no new household id to link to yet — the copy will surface in
        // the client list once it finishes.
        showFeedback(
          'Client copy started. The new client will appear shortly.',
          {
            variant: 'success',
          }
        );
      },
    });
  }

  return (
    <Stack
      spacing={2}
      direction="row"
      justifyContent="space-between"
      alignItems="end"
      noValidate
      component="form"
      onSubmit={handleSubmit(onClientCopy)}
    >
      <Box sx={{ flexGrow: 1 }}>
        <FormAwareTypeaheadSelectInput<CopyArchetypeFormShape>
          label="Select archetype client"
          fieldName={'householdId'}
          required
          options={options}
          disabled={isSubmitting || dataLoading}
          control={control}
        />
      </Box>
      <Box>
        <Button
          loading={isSubmitting}
          variant="primary"
          size="sm"
          type="submit"
        >
          Copy client
        </Button>
      </Box>
    </Stack>
  );
}
